import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ClipLoader from 'react-spinners/ClipLoader'
import { toast } from 'react-toastify'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import { OrganizationWorkData, WorkData, WorkListPage, defaultWorksPage } from '../types'
import { Trash2, Edit2 } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { useSearchWorksMutation, useDeleteWorkMutation } from '../store/api'
import { connect } from 'react-redux'
import moment from 'moment'
import { convertWorkType } from '../utils'
import { setWorksPage } from '../store/slices/grantSlice'

interface WorkListStateProps {
  worksPage: WorkListPage
}

interface WorkListDispatchProps {
  setWorksPage: (payload: WorkListPage) => void
}

type WorkListProps = WorkListStateProps & WorkListDispatchProps

const ITEMS_PER_PAGE = 10

const WorkList = ({ worksPage, setWorksPage }: WorkListProps): JSX.Element => {
  const navigate = useNavigate()

  const [sortField, setSortField] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [showAllItems, setShowAllItems] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState('')
  const [pageNumber, setPageNumber] = useState(0)
  const [selectedWork, setSelectedWork] = useState<OrganizationWorkData | null>(null)

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)
  const [isDeleteApiRequestInProgress, setIsDeleteApiRequestInProgress] = useState<boolean>(false)

  const [searchWorks] = useSearchWorksMutation()
  const [deleteWork] = useDeleteWorkMutation()

  const onDeleteWorkClicked = (work: OrganizationWorkData): void => {
    setSelectedWork(work)
  }

  const handleDelete = async (): Promise<void> => {
    if (selectedWork) {
      try {
        setIsDeleteApiRequestInProgress(true)
        await deleteWork(selectedWork.id).unwrap()
        setSelectedWork(null)
        setIsDeleteApiRequestInProgress(false)
        refreshWorkData()

        const modals = document.querySelectorAll('#deleteWorkModal')

        for (let i = 0; i < modals.length; i++) {
          modals[i]?.setAttribute('style', 'display: none')
        }

        document.querySelector('.modal-backdrop')?.remove()
        document.querySelector('body')?.classList.remove('modal-open')

        toast('Successfully deleted the work')
      } catch (error) {
        console.error('Failed to delete the work:', error)
        toast.error('Failed to delete the work')
        setIsDeleteApiRequestInProgress(false)
      }
    }
  }

  const refreshWorkData = useCallback(async () => {
    const sortString = sortField && sortDirection ? [sortDirection, sortField] : ['asc', 'id']

    try {
      setIsApiRequestInProgress(true)

      await searchWorks({
        data: {
          page: pageNumber,
          size: showAllItems ? 1000 : ITEMS_PER_PAGE,
          filters: {},
          sort: sortString,
          title: searchValue,
        },
      }).unwrap()

      setIsApiRequestInProgress(false)
    } catch (e) {
      console.error(e)
      setIsApiRequestInProgress(false)
    }
  }, [showAllItems, pageNumber, sortField, sortDirection, searchValue])

  useEffect(() => {
    setWorksPage(defaultWorksPage)
    refreshWorkData()
  }, [])

  useEffect(() => {
    refreshWorkData()
  }, [searchValue, sortField, sortDirection, showAllItems, pageNumber])

  const onPrevPageClick = useCallback(() => {
    if (worksPage?.first) {
      return
    }

    setPageNumber(pageNumber - 1)
  }, [pageNumber])

  const onNextPageClick = useCallback(() => {
    if (worksPage?.last) {
      return
    }

    setPageNumber(pageNumber + 1)
  }, [pageNumber])

  const works: OrganizationWorkData[] = useMemo(() => {
    return worksPage?.content
  }, [worksPage])

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <div className="row mt-3">
          <div className="col-12">
            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-6 border-top border-300">
              <div id="projectSummary">
                <div className="row align-items-end justify-content-between pb-4 g-3">
                  <div className="col-auto d-flex align-items-center">
                    <h3 style={{ whiteSpace: 'nowrap' }}>Work Products</h3>
                    {isApiRequestInProgress ? (
                      <div className="spinner-wrapper ms-3">
                        <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={20} />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="row align-items-center g-3 justify-content-between justify-content-start mb-3">
                  <div className="col col-auto">
                    <div className="search-box mb-2 mb-sm-0">
                      <form className="position-relative" data-bs-toggle="search" data-bs-display="static">
                        <input
                          className="form-control search-input search"
                          type="search"
                          placeholder="Search Work"
                          aria-label="Search"
                          value={searchValue}
                          onChange={(e) => {
                            setSearchValue(e.target.value)
                          }}
                        />
                        <span className="fas fa-search search-box-icon"></span>
                      </form>
                    </div>
                  </div>
                  {/*
                  <div className="col-auto">
                    <div className="btn-group me-1">
                      <button
                        className="btn dropdown-toggle mb-1 btn-primary"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        <span className="fas fa-plus me-2"></span>Add new work
                      </button>
                      <div className="dropdown-menu">
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            navigate('/works/add?type=journal-article')
                          }}
                        >
                          Journal Article
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            navigate('/works/add?type=book')
                          }}
                        >
                          Book
                        </button>
                      </div>
                    </div>
                  </div>
                    */}
                </div>

                <div className="table-responsive ms-n1 ps-1 scrollbar">
                  {works?.length ? (
                    <table className="table fs--1 mb-0 border-top border-200">
                      <thead>
                        <tr>
                          <th className="sort align-middle ps-3" scope="col" data-sort="award" style={{ width: '10%' }}>
                            Award
                          </th>
                          <th className="sort align-middle ps-3" scope="col" data-sort="doi" style={{ width: '10%' }}>
                            DOI
                          </th>
                          <th
                            className="sort white-space-nowrap align-middle ps-3"
                            scope="col"
                            data-sort="title"
                            style={{ width: '30%' }}
                          >
                            Title
                          </th>
                          <th
                            className="sort align-middle ps-3"
                            scope="col"
                            data-sort="publisher"
                            style={{ width: '10%' }}
                          >
                            Publisher
                          </th>
                          <th
                            className="sort align-middle ps-3"
                            scope="col"
                            data-sort="published"
                            style={{ width: '12%' }}
                          >
                            Published
                          </th>
                          <th className="sort align-middle ps-3" scope="col" data-sort="type" style={{ width: '10%' }}>
                            Type
                          </th>
                          <th className="sort align-middle ps-3" scope="col" data-sort="type" style={{ width: '10%' }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="list" id="project-summary-table-body">
                        {works.map((work) => (
                          <tr key={work.id} className="position-static">
                            <td className="align-middle white-space-nowrap ps-3 award">
                              {work.grantId && work.externalGrantId ? (
                                <button
                                  className="btn-link fw-normal fs--1"
                                  style={{ color: '#3874ff' }}
                                  onClick={() => {
                                    navigate(`/grants/${work.grantId}/details`)
                                  }}
                                >
                                  {work.externalGrantId}
                                </button>
                              ) : null}
                              {!work.grantId && work.externalGrantId ? (
                                <p className="mb-0 fs--1 text-900">{work.externalGrantId}</p>
                              ) : null}
                            </td>
                            <td className="align-middle time white-space-nowrap ps-3 doi">
                              <a
                                className="fw-normal fs--1"
                                href={`http://dx.doi.org/${work.workProduct.doi}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {work.workProduct.doi}
                              </a>
                            </td>
                            <td className="align-middle white-space-nowrap ps-3 title">
                              <button
                                className="btn-link mb-0 fs--1"
                                style={{ color: '#3874ff', whiteSpace: 'normal', textAlign: 'left' }}
                                onClick={() => {
                                  navigate(`/works/${work.id}/details`)
                                }}
                              >
                                {work.workProduct.title}
                              </button>
                            </td>
                            <td className="align-middle white-space-nowrap ps-3 publisher">
                              <p className="mb-0 fs--1 text-900">{work.workProduct.publisher}</p>
                            </td>
                            <td className="align-middle white-space-nowrap ps-3 published">
                              <p className="mb-0 fs--1 text-900">
                                {work.workProduct.publicationDate
                                  ? moment(work.workProduct.publicationDate).format('MMM, YYYY')
                                  : ''}
                              </p>
                            </td>
                            <td className="align-middle white-space-nowrap ps-3 type">
                              {work.workProduct.workProductType ? (
                                <p className="mb-0 fs--1 text-900">
                                  {convertWorkType(work.workProduct.workProductType)}
                                </p>
                              ) : null}
                            </td>
                            <td className="align-middle white-space-nowrap ps-3 type">
                              <div className="d-flex justify-content-start sort align-middle ps-3">
                                <Edit2
                                  className="me-3 text-success-300 pointer"
                                  size={18}
                                  onClick={() => navigate(`/works/${work.id}/edit`)}
                                />
                                <Trash2
                                  className="text-danger-300 pointer"
                                  data-bs-toggle="modal"
                                  data-bs-target="#deleteWorkModal"
                                  size={18}
                                  onClick={() => onDeleteWorkClicked(work)}
                                />
                                <div
                                  className="modal fade"
                                  id="deleteWorkModal"
                                  tabIndex={-1}
                                  aria-labelledby="verticallyCenteredModalLabel"
                                  style={{ display: 'none' }}
                                  aria-hidden="true"
                                >
                                  <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                      <div className="modal-header">
                                        <h5
                                          className="modal-title"
                                          id={`verticallyCenteredModalLabel`}
                                          style={{ whiteSpace: 'normal' }}
                                        >
                                          Do you really want to delete the{' '}
                                          {`${
                                            convertWorkType(
                                              selectedWork && selectedWork.workProduct.workProductType
                                                ? selectedWork.workProduct.workProductType
                                                : '',
                                            ) ?? 'work'
                                          }`.toLowerCase()}{' '}
                                          &quot;
                                          {selectedWork?.workProduct?.title}&quot;?
                                        </h5>
                                      </div>
                                      <div className="modal-footer">
                                        <button
                                          className="btn btn-primary"
                                          onClick={handleDelete}
                                          type="button"
                                          style={{ width: '90px' }}
                                        >
                                          {isDeleteApiRequestInProgress ? (
                                            <ClipLoader
                                              color="white"
                                              loading={isDeleteApiRequestInProgress}
                                              size={15}
                                            />
                                          ) : (
                                            'Delete'
                                          )}
                                        </button>
                                        <button
                                          className="btn btn-outline-primary"
                                          type="button"
                                          data-bs-dismiss="modal"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="my-7 text-center">No Work Products found</div>
                  )}
                </div>
                <div className="modal fade" id="filterModal" tabIndex={-1}></div>
                {works?.length ? (
                  <div className="row align-items-center justify-content-between py-2 pe-0 fs--1">
                    <div className="col-auto d-flex">
                      {worksPage.totalElements > 1 ? (
                        <p
                          className="mb-0 d-none d-sm-block me-3 fw-semi-bold text-900"
                          data-list-info="data-list-info"
                        >
                          {pageNumber * ITEMS_PER_PAGE + 1} to {pageNumber * ITEMS_PER_PAGE + worksPage.content.length}{' '}
                          <span className="text-600"> Items of </span>
                          {worksPage.totalElements}
                        </p>
                      ) : null}
                      {showAllItems ? (
                        <button
                          className="btn-link fw-semi-bold"
                          onClick={() => {
                            setShowAllItems(false)
                          }}
                        >
                          View Less<span className="fas fa-angle-right ms-1" data-fa-transform="down-1"></span>
                        </button>
                      ) : null}
                      {!showAllItems && worksPage.totalPages > 1 ? (
                        <button
                          className="btn-link fw-semi-bold"
                          onClick={() => {
                            setShowAllItems(true)
                            setPageNumber(0)
                          }}
                        >
                          View all<span className="fas fa-angle-right ms-1" data-fa-transform="down-1"></span>
                        </button>
                      ) : null}
                    </div>
                    {worksPage.totalPages > 1 ? (
                      <div className="col-auto d-flex">
                        <button
                          className="page-link"
                          data-list-pagination="prev"
                          disabled={worksPage.first}
                          onClick={() => {
                            onPrevPageClick()
                          }}
                        >
                          <span className="fas fa-chevron-left"></span>
                        </button>
                        <ul className="mb-0 pagination"></ul>
                        <button
                          className="page-link pe-0"
                          data-list-pagination="next"
                          disabled={worksPage.last}
                          onClick={() => {
                            onNextPageClick()
                          }}
                        >
                          <span className="fas fa-chevron-right"></span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </PrivateLayout>
  )
}

const mapStateToProps = (state: any): WorkListStateProps => {
  return {
    worksPage: state.grant.worksPage,
  }
}

const mapDispatchToProps = (dispatch: any): WorkListDispatchProps => {
  return {
    setWorksPage: (payload: WorkListPage) => dispatch(setWorksPage(payload)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkList)
